import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ForceGraph3D from 'react-force-graph-3d';
import * as THREE from 'three';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import HubIcon from '@mui/icons-material/Hub';
import MemoryIcon from '@mui/icons-material/Memory';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { agentApi } from '../../services/api';
import { LoadingSpinner } from '../common';
import type { AgentMemory, AgentMemoryListFilters, AgentToolMemory } from '../../types/agent';

type MemoryType = AgentMemory['memory_type'];
type GraphMode = 'agents' | 'types' | 'scope' | 'provenance';

type MemoryGraphNode = {
  id: string;
  label: string;
  kind: 'workspace' | 'agent' | 'type' | 'scope' | 'memory' | 'source';
  memoryType?: MemoryType;
  color: string;
  val: number;
  memory?: AgentMemory;
};

type MemoryGraphLink = {
  source: string;
  target: string;
  color: string;
  width: number;
};

const MEMORY_TYPE_META: Record<MemoryType, { label: string; color: string; shape: string }> = {
  SHORT_TERM: { label: 'Short Term', color: '#38bdf8', shape: 'Sphere' },
  LONG_TERM: { label: 'Long Term', color: '#22c55e', shape: 'Cube' },
  SEMANTIC: { label: 'Semantic', color: '#f59e0b', shape: 'Octahedron' },
  EPISODIC: { label: 'Episodic', color: '#f43f5e', shape: 'Cone' },
};

const TYPE_ORDER: MemoryType[] = ['SHORT_TERM', 'LONG_TERM', 'SEMANTIC', 'EPISODIC'];
const GRAPH_PAGE_SIZE = 100;

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
] as const;

const SCOPE_OPTIONS = [
  { value: 'private', label: 'Private' },
  { value: 'published', label: 'Published' },
] as const;

const NODE_OPTIONS = [
  { value: 'MEMORY', label: 'Memory' },
  { value: 'FOLDER', label: 'Folder' },
] as const;

const formatMemoryType = (type: string) =>
  MEMORY_TYPE_META[type as MemoryType]?.label || type.replaceAll('_', ' ');

const getScopeLabel = (memory: AgentMemory) =>
  memory.scope_visibility === 'published' ? 'Published' : 'Private';

const getAgentId = (memory: AgentMemory) => memory.agent_id ?? memory.agent;

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const truncate = (value: string, max = 150) =>
  value.length > max ? `${value.slice(0, max).trim()}...` : value;

const formatJsonPreview = (value: unknown, max = 150) => {
  if (
    value === undefined
    || value === null
    || (Array.isArray(value) && value.length === 0)
    || (typeof value === 'object' && !Array.isArray(value) && Object.keys(value as Record<string, unknown>).length === 0)
  ) {
    return '-';
  }

  try {
    return truncate(JSON.stringify(value), max);
  } catch {
    return truncate(String(value), max);
  }
};

const renderMultiValue = (labels: string[], fallback: string) => {
  if (!labels.length) return fallback;
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
};

function useElementSize(ref: React.RefObject<HTMLElement>) {
  const [size, setSize] = useState({ width: 900, height: 500 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    const update = () => {
      setSize({
        width: Math.max(Math.floor(element.clientWidth), 320),
        height: Math.max(Math.floor(element.clientHeight), 360),
      });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return size;
}

function buildMemoryGraph(memories: AgentMemory[], mode: GraphMode) {
  const nodes = new Map<string, MemoryGraphNode>();
  const links: MemoryGraphLink[] = [];
  const linkKeys = new Set<string>();

  const addNode = (node: MemoryGraphNode) => {
    if (!nodes.has(node.id)) nodes.set(node.id, node);
  };

  const addLink = (source: string, target: string, color: string, width = 1.1) => {
    const key = `${source}->${target}`;
    if (linkKeys.has(key)) return;
    linkKeys.add(key);
    links.push({ source, target, color, width });
  };

  addNode({
    id: 'workspace',
    label: 'Agent Memory',
    kind: 'workspace',
    color: '#eab308',
    val: 10,
  });

  const addAgentHub = (memory: AgentMemory) => {
    const agentId = getAgentId(memory) ?? 'unknown';
    const agentKey = `agent:${agentId}`;
    addNode({
      id: agentKey,
      label: memory.agent_name || `Agent ${agentId}`,
      kind: 'agent',
      color: '#14b8a6',
      val: 7,
    });
    addLink('workspace', agentKey, '#14b8a6', 0.9);
    return agentKey;
  };

  const addTypeHub = (type: MemoryType) => {
    const typeKey = `type:${type}`;
    addNode({
      id: typeKey,
      label: MEMORY_TYPE_META[type].label,
      kind: 'type',
      memoryType: type,
      color: MEMORY_TYPE_META[type].color,
      val: 7,
    });
    addLink('workspace', typeKey, MEMORY_TYPE_META[type].color, 1);
    return typeKey;
  };

  const addScopeHub = (scope: string) => {
    const scopeKey = `scope:${scope.toLowerCase()}`;
    addNode({
      id: scopeKey,
      label: scope,
      kind: 'scope',
      color: scope === 'Published' ? '#a78bfa' : '#94a3b8',
      val: 7,
    });
    addLink('workspace', scopeKey, scope === 'Published' ? '#a78bfa' : '#94a3b8', 1);
    return scopeKey;
  };

  memories.forEach((memory) => {
    const scope = getScopeLabel(memory);
    const memoryKey = `memory:${memory.id}`;
    const typeMeta = MEMORY_TYPE_META[memory.memory_type];
    const importance = Math.max(memory.importance_score || 0, 0.15);

    addNode({
      id: memoryKey,
      label: `${formatMemoryType(memory.memory_type)} - ${truncate(memory.content || memory.name || `Memory ${memory.id}`, 80)}`,
      kind: 'memory',
      memoryType: memory.memory_type,
      color: typeMeta?.color || '#f8fafc',
      val: 3 + importance * 5,
      memory,
    });

    if (mode === 'types') {
      addLink(addTypeHub(memory.memory_type), memoryKey, typeMeta?.color || '#f8fafc', 1.2 + importance);
      return;
    }

    if (mode === 'scope') {
      addLink(addScopeHub(scope), memoryKey, scope === 'Published' ? '#a78bfa' : '#94a3b8', 1.1 + importance);
      return;
    }

    addLink(addAgentHub(memory), memoryKey, '#64748b', 1 + importance);

    if (mode === 'provenance') {
      if (memory.source_session_id) {
        const sourceKey = `session:${memory.source_session_id}`;
        addNode({
          id: sourceKey,
          label: `Session ${memory.source_session_id}`,
          kind: 'source',
          color: '#fb7185',
          val: 4,
        });
        addLink(sourceKey, memoryKey, '#fb7185', 1.1);
      }
      if (memory.source_execution_id) {
        const sourceKey = `execution:${memory.source_execution_id}`;
        addNode({
          id: sourceKey,
          label: `Execution ${memory.source_execution_id}`,
          kind: 'source',
          color: '#60a5fa',
          val: 4,
        });
        addLink(sourceKey, memoryKey, '#60a5fa', 1.1);
      }
    }
  });

  return { nodes: Array.from(nodes.values()), links };
}

function createGraphNodeObject(node: MemoryGraphNode) {
  const size = Math.max(2.5, node.val);
  let geometry: THREE.BufferGeometry;

  if (node.kind === 'workspace') {
    geometry = new THREE.TorusKnotGeometry(size * 0.55, size * 0.16, 72, 10);
  } else if (node.kind === 'agent') {
    geometry = new THREE.CylinderGeometry(size * 0.45, size * 0.45, size * 0.8, 28);
  } else if (node.kind === 'type') {
    geometry = new THREE.TetrahedronGeometry(size * 0.55);
  } else if (node.kind === 'scope') {
    geometry = new THREE.DodecahedronGeometry(size * 0.45);
  } else if (node.kind === 'source') {
    geometry = new THREE.IcosahedronGeometry(size * 0.42);
  } else if (node.memoryType === 'LONG_TERM') {
    geometry = new THREE.BoxGeometry(size * 0.65, size * 0.65, size * 0.65);
  } else if (node.memoryType === 'SEMANTIC') {
    geometry = new THREE.OctahedronGeometry(size * 0.55);
  } else if (node.memoryType === 'EPISODIC') {
    geometry = new THREE.ConeGeometry(size * 0.48, size * 0.9, 28);
  } else {
    geometry = new THREE.SphereGeometry(size * 0.42, 32, 18);
  }

  const material = new THREE.MeshStandardMaterial({
    color: node.color,
    emissive: node.color,
    emissiveIntensity: node.kind === 'memory' ? 0.2 : 0.12,
    roughness: 0.45,
    metalness: 0.18,
  });

  return new THREE.Mesh(geometry, material);
}

function summarizeMemories(memories: AgentMemory[], totalCount: number) {
  const byType = TYPE_ORDER.reduce<Record<MemoryType, number>>((acc, type) => {
    acc[type] = 0;
    return acc;
  }, {} as Record<MemoryType, number>);
  const agents = new Set<string>();
  let published = 0;
  let importanceTotal = 0;

  memories.forEach((memory) => {
    byType[memory.memory_type] += 1;
    const agentId = getAgentId(memory);
    if (agentId !== undefined && agentId !== null) agents.add(String(agentId));
    if (memory.scope_visibility === 'published') published += 1;
    importanceTotal += memory.importance_score || 0;
  });

  return {
    byType,
    total: totalCount,
    agents: agents.size,
    published,
    averageImportance: memories.length ? importanceTotal / memories.length : 0,
  };
}

export const AgentMemoryWorkspacePage: React.FC = () => {
  const graphRef = useRef<any>(null);
  const graphShellRef = useRef<HTMLDivElement>(null);
  const graphSize = useElementSize(graphShellRef);

  const [search, setSearch] = useState('');
  const [agentFilter, setAgentFilter] = useState<string[]>([]);
  const [memoryTypes, setMemoryTypes] = useState<MemoryType[]>([]);
  const [statusFilter, setStatusFilter] = useState<Array<'active' | 'expired'>>(['active']);
  const [scopeFilter, setScopeFilter] = useState<Array<'private' | 'published'>>([]);
  const [nodeTypes, setNodeTypes] = useState<Array<'MEMORY' | 'FOLDER'>>(['MEMORY']);
  const [importanceRange, setImportanceRange] = useState<number[]>([0, 1]);
  const [ordering, setOrdering] = useState('-created_at');
  const [graphMode, setGraphMode] = useState<GraphMode>('agents');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedMemory, setSelectedMemory] = useState<AgentMemory | null>(null);
  const [toolPage, setToolPage] = useState(0);
  const [toolRowsPerPage, setToolRowsPerPage] = useState(10);
  const [selectedToolMemory, setSelectedToolMemory] = useState<AgentToolMemory | null>(null);

  const filters = useMemo<AgentMemoryListFilters>(() => {
    const trimmedSearch = search.trim();
    return {
      agent_id: agentFilter.length ? agentFilter.map((id) => Number(id)) : undefined,
      query: trimmedSearch || undefined,
      memory_type: memoryTypes.length ? memoryTypes : undefined,
      node_type: nodeTypes.length ? nodeTypes : undefined,
      scope_visibility: scopeFilter.length ? scopeFilter : undefined,
      status: statusFilter.length ? statusFilter : undefined,
      min_importance: importanceRange[0] > 0 ? importanceRange[0] : undefined,
      max_importance: importanceRange[1] < 1 ? importanceRange[1] : undefined,
      ordering,
    };
  }, [agentFilter, importanceRange, memoryTypes, nodeTypes, ordering, scopeFilter, search, statusFilter]);

  const toolMemoryFilters = useMemo(() => {
    const trimmedSearch = search.trim();
    return {
      agent_id: agentFilter.length ? agentFilter.map((id) => Number(id)) : undefined,
      query: trimmedSearch || undefined,
      ordering: '-created_at',
    };
  }, [agentFilter, search]);

  useEffect(() => {
    setPage(0);
  }, [filters]);

  useEffect(() => {
    setToolPage(0);
  }, [toolMemoryFilters]);

  const agentsQuery = useQuery({
    queryKey: ['agents-for-memory-workspace'],
    queryFn: () => agentApi.list(1, 100),
  });

  const memoriesQuery = useQuery({
    queryKey: ['agent-memory-workspace', page, rowsPerPage, filters],
    queryFn: () => agentApi.listMemories(page + 1, rowsPerPage, filters),
    placeholderData: (previous) => previous,
  });

  const graphQuery = useQuery({
    queryKey: ['agent-memory-workspace-graph', filters],
    queryFn: () => agentApi.listMemories(1, GRAPH_PAGE_SIZE, filters),
    placeholderData: (previous) => previous,
  });

  const toolMemoriesQuery = useQuery({
    queryKey: ['agent-tool-memory-workspace', toolPage, toolRowsPerPage, toolMemoryFilters],
    queryFn: () => agentApi.listToolMemories(toolPage + 1, toolRowsPerPage, toolMemoryFilters),
    placeholderData: (previous) => previous,
  });

  const tableRows = memoriesQuery.data?.results || [];
  const toolRows = toolMemoriesQuery.data?.results || [];
  const graphRows = graphQuery.data?.results || tableRows;
  const graphData = useMemo(() => buildMemoryGraph(graphRows, graphMode), [graphRows, graphMode]);
  const summary = useMemo(
    () => summarizeMemories(graphRows, graphQuery.data?.count ?? memoriesQuery.data?.count ?? 0),
    [graphRows, graphQuery.data?.count, memoriesQuery.data?.count],
  );

  useEffect(() => {
    if (!graphRef.current || graphData.nodes.length === 0) return;
    const graph = graphRef.current;
    graph.d3Force('charge')?.strength(-110);
    graph.d3Force('link')?.distance(graphMode === 'provenance' ? 74 : 62);
    graph.cameraPosition({ x: 0, y: 0, z: 230 }, { x: 0, y: 0, z: 0 }, 700);
    const timer = window.setTimeout(() => graph.zoomToFit?.(650, 56), 900);
    return () => window.clearTimeout(timer);
  }, [graphData.links.length, graphData.nodes.length, graphMode]);

  const isLoading = memoriesQuery.isLoading && !memoriesQuery.data;
  const isFetching = memoriesQuery.isFetching || graphQuery.isFetching || toolMemoriesQuery.isFetching;
  const memoryCount = memoriesQuery.data?.count || 0;
  const toolMemoryCount = toolMemoriesQuery.data?.count || 0;
  const agentsForFilter = agentsQuery.data?.results || [];
  const selectedAgentOptions = useMemo(
    () => agentsForFilter.filter((agent) => agentFilter.includes(String(agent.id))),
    [agentFilter, agentsForFilter],
  );

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleToolRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setToolRowsPerPage(Number(event.target.value));
    setToolPage(0);
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MemoryIcon color="primary" />
            <Typography variant="h4" fontWeight={700}>
              Agent Memory
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Durable agent memories across agents, scopes, provenance, and memory types.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            component={RouterLink}
            to="/ai/agents"
            variant="outlined"
            startIcon={<OpenInNewIcon />}
          >
            Agents
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => {
              memoriesQuery.refetch();
              graphQuery.refetch();
              toolMemoriesQuery.refetch();
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={1.5}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
            <Typography variant="caption" color="text.secondary">Matching</Typography>
            <Typography variant="h5" fontWeight={700}>{summary.total}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
            <Typography variant="caption" color="text.secondary">Agents</Typography>
            <Typography variant="h5" fontWeight={700}>{summary.agents}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
            <Typography variant="caption" color="text.secondary">Avg Importance</Typography>
            <Typography variant="h5" fontWeight={700}>{summary.averageImportance.toFixed(2)}</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
            <Typography variant="caption" color="text.secondary">Published</Typography>
            <Typography variant="h5" fontWeight={700}>{summary.published}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <FilterAltIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>Filters</Typography>
        </Stack>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              size="small"
              options={agentsForFilter}
              value={selectedAgentOptions}
              loading={agentsQuery.isLoading}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, state) => {
                const query = state.inputValue.trim().toLowerCase();
                if (!query) return options;
                return options.filter((option) =>
                  `${option.name} ${option.agent_type || ''} ${option.description || ''}`
                    .toLowerCase()
                    .includes(query),
                );
              }}
              onChange={(_event, nextAgents) => setAgentFilter(nextAgents.map((agent) => String(agent.id)))}
              renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                    <ListItemText primary={option.name} secondary={option.agent_type || undefined} />
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.slice(0, 2).map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={key}
                      {...tagProps}
                      label={option.name}
                      size="small"
                    />
                  );
                }).concat(
                  value.length > 2
                    ? [
                        <Chip
                          key="agent-filter-overflow"
                          label={`+${value.length - 2}`}
                          size="small"
                        />,
                      ]
                    : [],
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Agent"
                  placeholder={selectedAgentOptions.length ? '' : 'All agents'}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Type</InputLabel>
              <Select
                multiple
                displayEmpty
                label="Type"
                value={memoryTypes}
                onChange={(event) => setMemoryTypes(
                  typeof event.target.value === 'string'
                    ? event.target.value.split(',') as MemoryType[]
                    : event.target.value as MemoryType[],
                )}
                renderValue={(selected) => renderMultiValue(
                  (selected as MemoryType[]).map((type) => MEMORY_TYPE_META[type].label),
                  'All',
                )}
              >
                {TYPE_ORDER.map((type) => (
                  <MenuItem value={type} key={type}>
                    <Checkbox checked={memoryTypes.includes(type)} size="small" />
                    <ListItemText primary={MEMORY_TYPE_META[type].label} secondary={MEMORY_TYPE_META[type].shape} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Status</InputLabel>
              <Select
                multiple
                displayEmpty
                label="Status"
                value={statusFilter}
                onChange={(event) => setStatusFilter(
                  typeof event.target.value === 'string'
                    ? event.target.value.split(',') as Array<'active' | 'expired'>
                    : event.target.value as Array<'active' | 'expired'>,
                )}
                renderValue={(selected) => renderMultiValue(
                  (selected as Array<'active' | 'expired'>).map((value) => STATUS_OPTIONS.find((option) => option.value === value)?.label || value),
                  'All',
                )}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem value={option.value} key={option.value}>
                    <Checkbox checked={statusFilter.includes(option.value)} size="small" />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Scope</InputLabel>
              <Select
                multiple
                displayEmpty
                label="Scope"
                value={scopeFilter}
                onChange={(event) => setScopeFilter(
                  typeof event.target.value === 'string'
                    ? event.target.value.split(',') as Array<'private' | 'published'>
                    : event.target.value as Array<'private' | 'published'>,
                )}
                renderValue={(selected) => renderMultiValue(
                  (selected as Array<'private' | 'published'>).map((value) => SCOPE_OPTIONS.find((option) => option.value === value)?.label || value),
                  'All',
                )}
              >
                {SCOPE_OPTIONS.map((option) => (
                  <MenuItem value={option.value} key={option.value}>
                    <Checkbox checked={scopeFilter.includes(option.value)} size="small" />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 1.5 }}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Node</InputLabel>
              <Select
                multiple
                displayEmpty
                label="Node"
                value={nodeTypes}
                onChange={(event) => setNodeTypes(
                  typeof event.target.value === 'string'
                    ? event.target.value.split(',') as Array<'MEMORY' | 'FOLDER'>
                    : event.target.value as Array<'MEMORY' | 'FOLDER'>,
                )}
                renderValue={(selected) => renderMultiValue(
                  (selected as Array<'MEMORY' | 'FOLDER'>).map((value) => NODE_OPTIONS.find((option) => option.value === value)?.label || value),
                  'All',
                )}
              >
                {NODE_OPTIONS.map((option) => (
                  <MenuItem value={option.value} key={option.value}>
                    <Checkbox checked={nodeTypes.includes(option.value)} size="small" />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Box sx={{ px: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Importance {importanceRange[0].toFixed(2)} - {importanceRange[1].toFixed(2)}
              </Typography>
              <Slider
                value={importanceRange}
                onChange={(_event, value) => setImportanceRange(value as number[])}
                min={0}
                max={1}
                step={0.05}
                valueLabelDisplay="auto"
              />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort</InputLabel>
              <Select
                label="Sort"
                value={ordering}
                onChange={(event) => setOrdering(event.target.value)}
              >
                <MenuItem value="-created_at">Newest</MenuItem>
                <MenuItem value="created_at">Oldest</MenuItem>
                <MenuItem value="-importance_score">Importance high</MenuItem>
                <MenuItem value="importance_score">Importance low</MenuItem>
                <MenuItem value="-access_count">Most accessed</MenuItem>
                <MenuItem value="agent__name">Agent name</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.4fr) minmax(280px, 0.6fr)' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        <Box
          ref={graphShellRef}
          sx={{
            position: 'relative',
            height: { xs: 420, md: 520 },
            overflow: 'hidden',
            bgcolor: '#05070d',
            border: '1px solid',
            borderColor: 'divider',
            '& canvas': {
              display: 'block',
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              zIndex: 2,
              top: 12,
              left: 12,
              right: 12,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
              alignItems: 'center',
              pointerEvents: 'none',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip icon={<HubIcon />} label={`${graphData.nodes.length} nodes`} size="small" />
              <Chip label={`${graphData.links.length} links`} size="small" />
            </Stack>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={graphMode}
              onChange={(_event, value) => value && setGraphMode(value)}
              sx={{ pointerEvents: 'auto', bgcolor: 'rgba(5,7,13,0.72)' }}
            >
              <ToggleButton value="agents">Agents</ToggleButton>
              <ToggleButton value="types">Types</ToggleButton>
              <ToggleButton value="scope">Scope</ToggleButton>
              <ToggleButton value="provenance">Provenance</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <ForceGraph3D
            ref={graphRef}
            width={graphSize.width}
            height={graphSize.height}
            graphData={graphData}
            backgroundColor="#05070d"
            nodeThreeObject={(node) => createGraphNodeObject(node as MemoryGraphNode)}
            nodeLabel={(node) => (node as MemoryGraphNode).label}
            linkColor={(link) => (link as MemoryGraphLink).color}
            linkWidth={(link) => (link as MemoryGraphLink).width}
            linkOpacity={0.48}
            linkDirectionalParticles={1}
            linkDirectionalParticleWidth={1.4}
            linkDirectionalParticleSpeed={0.004}
            cooldownTicks={120}
            onNodeClick={(node) => {
              const memory = (node as MemoryGraphNode).memory;
              if (memory) setSelectedMemory(memory);
            }}
          />
          {isFetching && (
            <LinearProgress sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }} />
          )}
        </Box>

        <Paper variant="outlined" sx={{ p: 2, minHeight: { lg: 520 } }}>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AccountTreeIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={700}>Shape Map</Typography>
            </Stack>
            {TYPE_ORDER.map((type) => (
              <Stack key={type} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      bgcolor: MEMORY_TYPE_META[type].color,
                      clipPath:
                        type === 'LONG_TERM'
                          ? 'inset(0 round 2px)'
                          : type === 'SEMANTIC'
                            ? 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)'
                            : type === 'EPISODIC'
                              ? 'polygon(50% 0, 100% 100%, 0 100%)'
                              : 'circle(50%)',
                    }}
                  />
                  <Typography variant="body2">{MEMORY_TYPE_META[type].label}</Typography>
                </Stack>
                <Chip size="small" label={summary.byType[type]} />
              </Stack>
            ))}
            <Divider />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Agent hubs</Typography>
              <Typography variant="body2">{summary.agents}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Published scope</Typography>
              <Typography variant="body2">{summary.published}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Graph limit</Typography>
              <Typography variant="body2">{GRAPH_PAGE_SIZE}</Typography>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      {memoriesQuery.isError ? (
        <Alert severity="error">Failed to load agent memories.</Alert>
      ) : isLoading ? (
        <LoadingSpinner />
      ) : (
        <Paper variant="outlined">
          <TableContainer sx={{ maxHeight: 620 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Memory</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Importance</TableCell>
                  <TableCell>Access</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.length > 0 ? tableRows.map((memory) => (
                  <TableRow
                    hover
                    key={memory.id}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedMemory(memory)}
                  >
                    <TableCell>
                      <Chip
                        label={formatMemoryType(memory.memory_type)}
                        size="small"
                        sx={{
                          bgcolor: MEMORY_TYPE_META[memory.memory_type].color,
                          color: '#020617',
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {memory.agent_name || `Agent ${getAgentId(memory) ?? '-'}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {memory.agent_type || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 520 }}>
                      <Typography variant="body2" noWrap>
                        {memory.name || memory.content}
                      </Typography>
                      {memory.name && (
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {memory.content}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getScopeLabel(memory)}
                        size="small"
                        variant={memory.scope_visibility === 'published' ? 'filled' : 'outlined'}
                        color={memory.scope_visibility === 'published' ? 'secondary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5} sx={{ minWidth: 90 }}>
                        <Typography variant="body2">{(memory.importance_score || 0).toFixed(2)}</Typography>
                        <LinearProgress
                          variant="determinate"
                          value={(memory.importance_score || 0) * 100}
                          sx={{ height: 5, borderRadius: 1 }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>{memory.access_count}</TableCell>
                    <TableCell>{formatDate(memory.created_at)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View memory details">
                        <IconButton
                          size="small"
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedMemory(memory);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
                        <MemoryIcon color="disabled" sx={{ fontSize: 42 }} />
                        <Typography color="text.secondary">No matching memories</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={memoryCount}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <HubIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={700}>Tool Memory</Typography>
            <Chip size="small" label={toolMemoryCount} />
          </Stack>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => toolMemoriesQuery.refetch()}
          >
            Refresh
          </Button>
        </Stack>

        {toolMemoriesQuery.isError ? (
          <Alert severity="error">Failed to load tool memories.</Alert>
        ) : toolMemoriesQuery.isLoading && !toolMemoriesQuery.data ? (
          <LoadingSpinner />
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Tool</TableCell>
                    <TableCell>Summary</TableCell>
                    <TableCell>Call ID</TableCell>
                    <TableCell>Execution</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {toolRows.length > 0 ? toolRows.map((memory) => (
                    <TableRow
                      hover
                      key={memory.id}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelectedToolMemory(memory)}
                    >
                      <TableCell>
                        <Chip
                          size="small"
                          label={memory.status}
                          color={memory.status === 'success' ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {memory.agent_name || `Agent ${memory.agent_id ?? '-'}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{memory.tool_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Step {memory.source_step_id ?? '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 520 }}>
                        <Typography variant="body2" noWrap>{memory.summary}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {formatJsonPreview(memory.entities, 120)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 190 }}>
                        <Typography variant="caption" noWrap component="div">
                          {memory.tool_call_id || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 190 }}>
                        <Typography variant="caption" noWrap component="div">
                          {memory.execution_id || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(memory.created_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View tool memory details">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedToolMemory(memory);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
                          <HubIcon color="disabled" sx={{ fontSize: 42 }} />
                          <Typography color="text.secondary">No matching tool memories</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={toolMemoryCount}
              page={toolPage}
              rowsPerPage={toolRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              onPageChange={(_event, nextPage) => setToolPage(nextPage)}
              onRowsPerPageChange={handleToolRowsPerPageChange}
            />
          </>
        )}
      </Paper>

      <Dialog
        open={!!selectedMemory}
        onClose={() => setSelectedMemory(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedMemory?.name || `Memory ${selectedMemory?.id ?? ''}`}
        </DialogTitle>
        <DialogContent>
          {selectedMemory && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={formatMemoryType(selectedMemory.memory_type)} color="primary" />
                <Chip label={getScopeLabel(selectedMemory)} variant="outlined" />
                <Chip label={selectedMemory.is_expired ? 'Expired' : 'Active'} color={selectedMemory.is_expired ? 'default' : 'success'} />
                {selectedMemory.node_type && <Chip label={selectedMemory.node_type} variant="outlined" />}
              </Stack>

              <Box>
                <Typography variant="caption" color="text.secondary">Agent</Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body1" fontWeight={700}>
                    {selectedMemory.agent_name || `Agent ${getAgentId(selectedMemory) ?? '-'}`}
                  </Typography>
                  {getAgentId(selectedMemory) && (
                    <Button
                      component={RouterLink}
                      to={`/ai/agents/${getAgentId(selectedMemory)}`}
                      size="small"
                      endIcon={<OpenInNewIcon fontSize="small" />}
                    >
                      Open
                    </Button>
                  )}
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="caption" color="text.secondary">Content</Typography>
                <Typography variant="body1" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                  {selectedMemory.content}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Importance</Typography>
                  <Typography variant="body1">{(selectedMemory.importance_score || 0).toFixed(2)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Access Count</Typography>
                  <Typography variant="body1">{selectedMemory.access_count}</Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body1">{formatDate(selectedMemory.created_at)}</Typography>
                </Grid>
                <Grid size={{ xs: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">Last Accessed</Typography>
                  <Typography variant="body1">{formatDate(selectedMemory.last_accessed)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Scope Namespace</Typography>
                  <Typography variant="body2">{selectedMemory.scope_namespace || '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Parent</Typography>
                  <Typography variant="body2">{selectedMemory.parent_id ?? '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Source Session</Typography>
                  <Typography variant="body2">{selectedMemory.source_session_id || '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Source Execution</Typography>
                  <Typography variant="body2">{selectedMemory.source_execution_id || '-'}</Typography>
                </Grid>
              </Grid>

              {selectedMemory.metadata && Object.keys(selectedMemory.metadata).length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Metadata</Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 1,
                        p: 1.5,
                        bgcolor: 'background.default',
                        maxHeight: 260,
                        overflow: 'auto',
                      }}
                    >
                      <Typography
                        component="pre"
                        variant="caption"
                        sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                      >
                        {JSON.stringify(selectedMemory.metadata, null, 2)}
                      </Typography>
                    </Paper>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedMemory(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!selectedToolMemory}
        onClose={() => setSelectedToolMemory(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedToolMemory?.tool_name || 'Tool Memory'}
        </DialogTitle>
        <DialogContent>
          {selectedToolMemory && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={selectedToolMemory.status}
                  color={selectedToolMemory.status === 'success' ? 'success' : 'error'}
                />
                <Chip label={selectedToolMemory.tool_name} variant="outlined" />
                {selectedToolMemory.tool_call_id && (
                  <Chip label={`Call ${selectedToolMemory.tool_call_id}`} variant="outlined" />
                )}
              </Stack>

              <Box>
                <Typography variant="caption" color="text.secondary">Summary</Typography>
                <Typography variant="body1" sx={{ mt: 0.75, whiteSpace: 'pre-wrap' }}>
                  {selectedToolMemory.summary}
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Agent</Typography>
                  <Typography variant="body2">
                    {selectedToolMemory.agent_name || `Agent ${selectedToolMemory.agent_id ?? '-'}`}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Created</Typography>
                  <Typography variant="body2">{formatDate(selectedToolMemory.created_at)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Session</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {selectedToolMemory.session_id || '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Execution</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {selectedToolMemory.execution_id || '-'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Source Step</Typography>
                  <Typography variant="body2">{selectedToolMemory.source_step_id ?? '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="caption" color="text.secondary">Tool Call ID</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {selectedToolMemory.tool_call_id || '-'}
                  </Typography>
                </Grid>
              </Grid>

              {[
                ['Entities', selectedToolMemory.entities],
                ['Memory Updates', selectedToolMemory.memory_updates],
                ['Arguments Preview', selectedToolMemory.arguments_preview],
                ['Result Preview', selectedToolMemory.result_preview],
                ['Metadata', selectedToolMemory.metadata],
              ].map(([label, value]) => (
                <Box key={label as string}>
                  <Typography variant="caption" color="text.secondary">{label as string}</Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      mt: 1,
                      p: 1.5,
                      bgcolor: 'background.default',
                      maxHeight: 220,
                      overflow: 'auto',
                    }}
                  >
                    <Typography
                      component="pre"
                      variant="caption"
                      sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {JSON.stringify(value, null, 2)}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedToolMemory(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentMemoryWorkspacePage;
